import React, { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthPage } from '../../hooks/useAuthPage';
import { hashPasswordClient } from '../../utils/passwordUtils';
import { FlowHeader, MARKETING_ORIGIN } from './TinycmsSignupFlow';

const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password)
  }
`;

/**
 * TinyCMS sign-in ("TinyCMS UX optimization review v3.zip", Signup Journey):
 * one column, the same header as signup, and a way into signup for someone
 * new. Session notices (signed out, session expired) come from the same
 * places the shared Login reads them.
 */
export function TinycmsLogin() {
  useAuthPage();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [hashing, setHashing] = useState(false);

  const redirectAfterAuth = () => {
    const target = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
    navigate(target || '/');
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) redirectAfterAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Messages left by logout(), the 401 handler and ErrorBoundary win; otherwise
  // say something only when a specific page turned the person away.
  useEffect(() => {
    const authError = sessionStorage.getItem('authError');
    if (authError) {
      sessionStorage.removeItem('authError');
      setNotice(
        /logged out|signed out/i.test(authError)
          ? { tone: 'ok', text: 'You’ve been signed out.' }
          : { tone: 'error', text: authError }
      );
      return;
    }
    const from = (location.state as { from?: string } | null)?.from;
    if (from && from !== '/') setNotice({ tone: 'error', text: 'Sign in to see that page.' });
    if (from) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [loginMutation, { loading }] = useMutation<{ login: string }>(LOGIN_MUTATION, {
    onCompleted: (data) => {
      login(data.login);
      redirectAfterAuth();
    },
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email) || !password) {
      setFormError('Enter your email and password.');
      return;
    }
    setFormError('');
    setHashing(true);
    try {
      await loginMutation({ variables: { email, password: await hashPasswordClient(password) } });
    } catch (err) {
      setFormError(
        err instanceof Error && err.message ? err.message : 'Sign in failed. Try again.'
      );
    } finally {
      setHashing(false);
    }
  };

  const busy = loading || hashing;
  return (
    <div className="flow">
      <FlowHeader
        right={
          <a className="flow-header-link" href={`${MARKETING_ORIGIN}/pricing`}>
            Pricing
          </a>
        }
      />
      <main className="flow-main flow-main-single">
        <form className="flow-signin" onSubmit={submit} noValidate>
          {notice ? (
            <div className="flow-notice" data-tone={notice.tone}>
              {notice.tone === 'ok' ? <span className="flow-status-dot" /> : null}
              {notice.text}
            </div>
          ) : null}
          <h1 className="flow-h1">Sign in</h1>
          <p className="flow-lede">Pick up where your agents left off.</p>
          <div className="flow-fields">
            <label className="flow-field">
              <span className="flow-label">Email</span>
              <input
                className="flow-input"
                type="email"
                value={email}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFormError('');
                }}
              />
            </label>
            <label className="flow-field">
              <span className="flow-label">Password</span>
              <input
                className="flow-input"
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFormError('');
                }}
              />
            </label>
          </div>
          {formError ? <p className="flow-error flow-submit-error">{formError}</p> : null}
          <button type="submit" className="flow-btn" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="flow-signin-foot">
            New to TinyCMS?{' '}
            <button type="button" className="flow-link" onClick={() => navigate('/signup')}>
              Create an account
            </button>
            . It’s free to start.
          </div>
        </form>
      </main>
    </div>
  );
}
